import { Shot } from '../types';

export interface ShotNamePattern {
  prefix: string;
  startNumber: number;
  step: number;
  digits: number;
}

/**
 * Анализирует имена шотов и определяет паттерн
 * @param shotNames - массив имён шотов (последние 2-3)
 * @returns паттерн или null если не найден
 */
export const analyzeShotNamePattern = (shotNames: string[]): ShotNamePattern | null => {
  if (shotNames.length < 2) return null;

  // Попытка извлечь числа из имён
  const patterns = shotNames.map(name => {
    // Ищем числовую часть в конце строки
    const match = name.match(/^(.+?)(\d+)$/);
    if (!match) return null;
    
    return {
      prefix: match[1],
      number: parseInt(match[2], 10),
      digits: match[2].length
    };
  });

  // Проверяем что все паттерны валидны
  if (patterns.some(p => p === null)) return null;

  // Проверяем что префиксы одинаковые
  const prefix = patterns[0]!.prefix;
  if (!patterns.every(p => p!.prefix === prefix)) return null;

  // Проверяем что количество цифр одинаковое
  const digits = patterns[0]!.digits;
  if (!patterns.every(p => p!.digits === digits)) return null;

  // Вычисляем шаг
  const numbers = patterns.map(p => p!.number);
  const steps = [];
  for (let i = 1; i < numbers.length; i++) {
    steps.push(numbers[i] - numbers[i - 1]);
  }

  // Проверяем что шаг одинаковый
  const step = steps[0];
  if (step <= 0) return null; // Шаг должен быть положительным
  if (!steps.every(s => s === step)) return null;

  // Вычисляем следующее начальное число
  const lastNumber = numbers[numbers.length - 1];
  const startNumber = lastNumber + step;

  return {
    prefix,
    startNumber,
    step,
    digits
  };
};

/**
 * Определяет паттерн на основе заполненных шотов
 * @param shots - все шоты
 * @returns паттерн или null
 */
export const detectShotNamePattern = (shots: Shot[]): ShotNamePattern | null => {
  // Берём последние 2-4 заполненных shotName
  const namedShots = shots
    .filter(s => s.shotName && s.shotName.trim())
    .slice(-4);

  if (namedShots.length < 2) return null;

  // Анализируем паттерн
  const shotNames = namedShots.map(s => s.shotName!);
  return analyzeShotNamePattern(shotNames);
};

/**
 * Проверяет, есть ли незаполненные shot names после заполненных
 * @param shots - все шоты
 * @returns true если есть незаполненные после заполненных
 */
export const hasUnfilledShotsAfterFilled = (shots: Shot[]): boolean => {
  const firstFilledIndex = shots.findIndex(s => s.shotName && s.shotName.trim());
  if (firstFilledIndex === -1) return false;

  const lastFilledIndex = shots.map((s, i) => ({ shot: s, index: i }))
    .filter(({ shot }) => shot.shotName && shot.shotName.trim())
    .pop()?.index ?? -1;

  if (lastFilledIndex === -1) return false;

  // Проверяем есть ли незаполненные после последнего заполненного
  const unfilledAfter = shots.slice(lastFilledIndex + 1).some(s => !s.shotName || !s.shotName.trim());
  
  return unfilledAfter;
};

/**
 * Генерирует имя шота на основе паттерна
 * @param pattern - паттерн
 * @param index - индекс относительно начала
 * @returns сгенерированное имя
 */
export const generateShotName = (pattern: ShotNamePattern, index: number): string => {
  const number = pattern.startNumber + (index * pattern.step);
  const paddedNumber = number.toString().padStart(pattern.digits, '0');
  return `${pattern.prefix}${paddedNumber}`;
};

